abstract class BaseWidget<TResultType, TConfigType extends BaseItemConfig> implements IWidget{

    container: any;
    itemConfig: TConfigType;
    abstract widgetType: WidgetType;
    result: TResultType;
    $reportContent: any;
    /** Disable drilldown functions - used to dashboards and widgets that cannot do drilldown */
    disableDrilldown = false;

    tab: WorkbookTab;
    tabOrWidget: ITabOrWidget;
    dashboardWidget: DashboardWidget;
    get isDashboardWidget() { return this.dashboardWidget ? true : false }
    get isDrilldownPossible() { return this.tabOrWidget.drilldownManager.isDrilldownPossible(); }

    /** Use this property when you want to change many properties at the same time and then save them together.
     * Be careful if you want to save properties that have custom functions or that are part of the drilldown. */
    saveSettingItemManually: boolean = false;

    constructor(container: any, tab: WorkbookTab, $reportContent, dashboardWidget: DashboardWidget) {
        this.container = container;
        this.tab = tab;
        this.tabOrWidget = dashboardWidget ?? tab;
        this.dashboardWidget = dashboardWidget;

        this.$reportContent = $reportContent;
    }

    abstract setup();
    abstract setupSettings();
    abstract repaint();
    abstract runSetup();

    executeDrilldown(tabValues: Array<FilterValuesVM>) {
        let self = this;
        
        let model = self.tabOrWidget.getBaseSubmitObject();
        model.drilldownId = self.tabOrWidget.drilldownManager.activeDrilldownId;
        model.nextDrilldown = self.tabOrWidget.drilldownManager.getNextDrilldownObject();
        model.tabValues = [...new Map(tabValues.map(item => [item.name, item])).values()];//distinct by name
        let itemConfigString = irGlobal.jsonStringfy(ko.toJS(this.itemConfig));
        model.config = itemConfigString;

        //This also prevents double click or clicking or other areas
        IrUtils.showLoadPanel("Loading: Drilldown");

        irGlobal.updateFromServer({},
            urlConfigs.workBook + "NavigateToDrillDown",
            model,
            function (data) {
                IrUtils.hideLoadPanel();
                self.updateOutputAfterDrilldown(data);
            },
            function (textStatus, errorThrown, errorItem: IError) {
                errorItem.userMessage = "Failed to navigate to drilldown";
                irGlobal.defaultErrorFunction(errorItem.userMessage, textStatus, errorThrown);
                IrUtils.hideLoadPanel();
            },
            true,
            "POST",
            false);
    }

    private settingsSubscriptions: Array<KnockoutSubscription> = [];

    disposeSettingsSubscriptions() {
        for (let subscription of this.settingsSubscriptions) {
            subscription.dispose();
        }
        this.settingsSubscriptions = [];
    }

    protected createSettingItem(property: KnockoutObservable<any>, propertyName: string, mode: UpdateSettingMode, customFunction: (newValue: any, widget: IWidget, isActiveDrilldown: boolean) => void = null) {
        let self = this;
        let subscription = property.subscribe(function (newValue) {
            //console.log(`Property: ${propertyName} Changed to: ${newValue}`);
            if (!self.saveSettingItemManually) {
                self.saveSettings(function () {
                    if (mode === UpdateSettingMode.Reload) {
                        self.tabOrWidget.refresh();
                    }
                    else {
                        let activeDrilldownIndex = self.tabOrWidget.drilldownManager.activeDrilldownIndex();

                        let sameWidgetType = self.tabOrWidget.widgetType() === self.tabOrWidget.drilldownManager.getActiveWidget().widgetType;

                        for (let drilldownViewListItem of self.tabOrWidget.drilldownManager.drilldownViewList()) {
                            if (!sameWidgetType && drilldownViewListItem.drilldownIndex !== activeDrilldownIndex) {
                                continue;
                            }

                            const widgetDrilldown = drilldownViewListItem.widget();
                            if (drilldownViewListItem.drilldownIndex > 0) {
                                self.setPropertyByName(widgetDrilldown.itemConfig, propertyName, newValue);
                                if (sameWidgetType) {
                                    //Save settings for drilldown levels
                                    widgetDrilldown.saveSettings(null, self.tabOrWidget.drilldownManager.getDrilldown(drilldownViewListItem.drilldownIndex).id);
                                }
                            }
                            switch (mode) {
                                case UpdateSettingMode.Setup:
                                    widgetDrilldown.runSetup();
                                    break;
                                case UpdateSettingMode.Repaint:
                                    widgetDrilldown.repaint();
                                    break;
                                case UpdateSettingMode.Custom:
                                    customFunction(newValue, widgetDrilldown, drilldownViewListItem.drilldownIndex === activeDrilldownIndex);
                                    break;
                                case UpdateSettingMode.Automatic:
                                    break;
                            }
                        }
                    }
                });
            }
        });

        self.settingsSubscriptions.push(subscription);
    }

    repaintForResize() {
        //Most of the widgets don't need to repaint on resize. 
    }

    repaintAllDrilldownLevels() {
        let self = this;
        for (let drilldownViewListItem of self.tabOrWidget.drilldownManager.drilldownViewList()) {
            drilldownViewListItem.widget().repaintForResize();
        }
    }

    private setPropertyByName(object, propertyName: string, value) {
        if (!propertyName.includes(".")) {
            object[propertyName](value);
        }
        else {
            let propertyNames = propertyName.split(".");
            this.setPropertyByName(object[propertyNames[0]], propertyNames[1], value);
        }
    }

    set$ReportContent($reportContent) {
        this.$reportContent = $($reportContent);
    }

    public saveSettings(successFunction: Function = null, drilldownId?: number) {
        this.saveSettingItemManually = false;
        let self = this;

        let itemConfigJS = ko.toJS(this.itemConfig);
        let submitObject = this.tabOrWidget.getBaseSubmitObject();
        let url = urlConfigs.workBook + "SaveReportSettings";

        if (workbook.workbookConfig.activeTab().tabType() === TabTypeEnum.Dashboard) {
            url = urlConfigs.dashboard + "SaveWidgetConfig";
            submitObject.config = irGlobal.jsonStringfy(itemConfigJS, this.widgetType !== WidgetType.Filter);
        }
        else {
            submitObject.reportConfig = irGlobal.jsonStringfy(itemConfigJS);
        }

        let sameWidgetType = self.tabOrWidget.widgetType() === self.tabOrWidget.drilldownManager.getActiveWidget().widgetType;
        if (!sameWidgetType && self.tabOrWidget.drilldownManager.isDrilldown) {
            drilldownId = self.tabOrWidget.drilldownManager.activeDrilldownId;
        }
        
        if (drilldownId === DrilldownManager.rootDrilldownId) {
            drilldownId = null;
        }

        if (drilldownId) {
            submitObject.drilldownId = drilldownId;
        }

        let async = submitObject.drilldownId ? true : false;

        irGlobal.updateFromServer({},
            url,
            submitObject,
            successFunction ? successFunction : function () { },
            function (textStatus, errorThrown, errorItem: IError) {
                errorItem.userMessage = "Failed to save settings";
                irGlobal.defaultErrorFunction(errorItem.userMessage, textStatus, errorThrown);
            },
            true, "POST", undefined, undefined, undefined, async);
    }

    getWidget<TWidget>() {
        return this;
    }

    abstract setResult(result);
    abstract setConfig(config);

    setResultAndConfig(result, isDrilldown = false) {
        if (result.itemConfig) {
            this.setConfig(result.itemConfig);
        }

        result.itemConfig = null;
        this.setResult(result);

        if (!isDrilldown && this.itemConfig) {
            if (!this.isDashboardWidget) {
                this.tabOrWidget.itemConfig(this.itemConfig);
            }
            else {
                this.dashboardWidget.itemConfig(this.itemConfig);
            }
        }

        if (!isDrilldown || this.widgetType !== this.tabOrWidget.widgetType()) {
            this.setupSettings();
        }
    }

    private updateOutputAfterDrilldown(data) {
        let self = this;

        let tabOrWidget = this.tabOrWidget;
        let drilldownIndex = tabOrWidget.drilldownManager.activeDrilldownIndex() + 1;
        let name = tabOrWidget.drilldownManager.drilldownList()[drilldownIndex].name;

        let dashboarWidget = tabOrWidget.tabOrWidget === TabOrWidgetEnum.Widget ? tabOrWidget as DashboardWidget : undefined;

        let reportWidget = BaseWidget.createWidget(null, workbook.workbookConfig.activeTab(), dashboarWidget, data.widgetType);
        reportWidget.setResultAndConfig(data, true);

        let drilldownViewItem: DrilldownViewListItemVM;

        let templateFunction = function () {
            let $widget = BaseWidget.getWidgetElement(reportWidget);
            return $widget;
        };

        drilldownViewItem = new DrilldownViewListItemVM(name, drilldownIndex, reportWidget, templateFunction);

        tabOrWidget.drilldownManager.drilldownViewList.push(drilldownViewItem);

        let previousDrilldown = tabOrWidget.drilldownManager.drilldownViewList()[drilldownIndex - 1];

        let previousFilters = createInstances(Filter, data.drilldownFilters as Array<any>);
        previousDrilldown.drilldownFilters(previousFilters);

        let drilldownFilters = createInstances(Filter, data.drilldownFilters as Array<any>);
        drilldownViewItem.filters(drilldownFilters);

        tabOrWidget.drilldownManager.activeDrilldownIndex(drilldownIndex);
        workbook.workbookConfig.workbookHelper.goToDrilldownToolbarTab();
        Ir.Components.Workbook.WorkbookSideTabs.goToDrilldownTab();
    }

    static createWidget(container: any, tab: WorkbookTab, dashboardWidget?: DashboardWidget, widgetType?: WidgetType): IWidget {
        if (widgetType === undefined || widgetType === null) {
            //If the widgetType is not passed, we need to get it from the dashboardWidget or tab
            widgetType = (dashboardWidget?.widgetType() ?? tab.widgetType());
        }
        switch (widgetType) {
            case WidgetType.Report:
                return new ReportWidget(container, tab, null, dashboardWidget);
            case WidgetType.Chart:
                return new ChartWidget(container, tab, null, dashboardWidget);
            case WidgetType.Pivot:
                return new PivotWidget(container, tab, null, dashboardWidget);
            case WidgetType.Filter:
                return new FilterWidget(container, tab, null, dashboardWidget);
            case WidgetType.Gauge:
                return new GaugeWidget(container, tab, null, dashboardWidget);
            case WidgetType.KPI:
                return new KPIWidget(container, tab, null, dashboardWidget);
            case WidgetType.Map:
                return new MapWidget(container, tab, null, dashboardWidget);
            case WidgetType.RichText:
                return new RichTextWidget(container, tab, null, dashboardWidget);
            case WidgetType.TreeMap:
                return new TreeMapWidget(container, tab, null, dashboardWidget);
            case WidgetType.Funnel:
                return new FunnelWidget(container, tab, null, dashboardWidget);
        }
    }

    static async refreshWidget(tabOrWidget: ITabOrWidget) {

        if (tabOrWidget.drilldownManager.isDifferentWidgetType) {
            tabOrWidget.drilldownManager.refresh();
            return;
        }

        if (tabOrWidget.tabOrWidget === TabOrWidgetEnum.Widget || (tabOrWidget as WorkbookTab).tabType() !== TabTypeEnum.Dashboard) {
            //Go back to the root level before refreshing
            tabOrWidget.drilldownManager.rollUp();
        }

        let $loading = $(`<loading></loading>`);
        ko.applyBindings({}, $loading[0]);
        $(tabOrWidget.widgetContent).replaceWith($loading);
        tabOrWidget.widgetContent = $loading;

        tabOrWidget.getWidgetData(function (result, error, errorMessage) {
            tabOrWidget.widget.setResult(result);

            let $widget = BaseWidget.getWidgetElement(tabOrWidget.widget);
            $(tabOrWidget.widgetContent).replaceWith($widget);
            tabOrWidget.widgetContent = $widget;
        });
    }

    static getWidgetElement(widget: IWidget) {
        let $widget = $(`<widget params="config: config, widget: widget"></widget>`);
        ko.applyBindings({ config: workbook.workbookConfig, widget: widget }, $widget[0]);
        return $widget;
    }

    print() { }
    exportToPDF(isLandscape: boolean, templateId: number) { }
    exportToXLSX(withoutHeaders: boolean) { }
    exportToCSV(withoutHeaders: boolean) { }

    static getWidgetIcon(type: WidgetType): string {
        switch (type) {
            case WidgetType.Report:
                return "list_alt";
            case WidgetType.Chart:
                return "equalizer";
            case WidgetType.Pivot:
                return "pivot_table_chart";
            case WidgetType.RichText:
                return "notes";
            case WidgetType.KPI:
                return "traffic";
            case WidgetType.Filter:
                return "filter_alt";
            case WidgetType.Gauge:
                return "speed";
            case WidgetType.Map:
                return "map";
            case WidgetType.TreeMap:
                return "view_quilt";
            case WidgetType.Funnel:
                return "signal_wifi_0_bar";
            default:
                return "";
        }
    }
    static getWidgetText(type: WidgetType): string {
        switch (type) {
            case WidgetType.RichText:
                return "HTML";
            case WidgetType.TreeMap:
                return "Tree Map";
            default:
                return WidgetType[type];
        }
    }
}